import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { constructNameEN } from '@/employee/employee-name-helper';
import { handleMutationResult } from '@/helpers/toastHelper';
import {
    useDeleteLeaveTypesMutation,
    useGetEmployeesByIdsQuery,
    useGetLocationsQuery,
    useLeaveTypeDeleteValidationQuery
} from '@/services/gql/graphql.generated';
import { createErrorToast } from '@/services/notification/ToastService';
import { format } from 'date-fns';
import { FC, useEffect, useMemo } from 'react';
import strings from '../admin.translations';
import { LeaveTypeRow } from './leaveTypes.types';

interface LeaveTypeDeleteDialogProps {
  leaveType: LeaveTypeRow | null;
  open: boolean;
  onClose: () => void;
  onDeleted: () => Promise<void> | void;
}

export const LeaveTypeDeleteDialog: FC<LeaveTypeDeleteDialogProps> = ({ leaveType, open, onClose, onDeleted }) => {
  const boundaryDate = format(new Date(), 'yyyy-MM-dd');
  const [{ fetching: deletingLeaveType }, deleteLeaveTypes] = useDeleteLeaveTypesMutation();
  // Start validation after the dialog opens. Auto-starting this query during the open render caused
  // a visible background repaint, so we keep it paused and reexecute it once the modal is mounted.
  const [
    { data: deleteValidationData, fetching: deleteValidationLoading, error: deleteValidationError },
    reexecuteDeleteValidation
  ] = useLeaveTypeDeleteValidationQuery({
    variables: leaveType
      ? {
          leaveTypeId: leaveType.id,
          boundaryDate
        }
      : undefined,
    pause: true,
    requestPolicy: 'network-only'
  });

  useEffect(() => {
    if (!open || !leaveType) {
      return;
    }

    reexecuteDeleteValidation({ requestPolicy: 'network-only' });
  }, [leaveType, open, reexecuteDeleteValidation]);

  useEffect(() => {
    if (!deleteValidationError || !open) {
      return;
    }

    createErrorToast(strings.deleteLeaveTypeDialog.validationError);
    onClose();
  }, [deleteValidationError, onClose, open]);

  const handleDelete = async () => {
    if (!leaveType || deleteValidationLoading || hasUsagesOnOrAfterBoundary) {
      return;
    }

    const result = await deleteLeaveTypes({ ids: [leaveType.id] });

    await handleMutationResult(result, {
      successMessage: strings.deleteLeaveTypeDialog.deleteSuccess,
      errorMessage: strings.deleteLeaveTypeDialog.deleteError,
      customSuccessCheck: data => data?.deleteLeaveTypes === true,
      onSuccess: async () => {
        onClose();
        await onDeleted();
      }
    });
  };

  const hasUsagesBeforeBoundary = deleteValidationData?.leaveTypeDeleteValidation.hasUsagesBeforeBoundary ?? false;
  const usagesOnOrAfterBoundary = deleteValidationData?.leaveTypeDeleteValidation.usagesOnOrAfterBoundary ?? [];
  const hasUsagesOnOrAfterBoundary = usagesOnOrAfterBoundary.length > 0;
  const employeeIds = useMemo(
    () => Array.from(new Set(usagesOnOrAfterBoundary.map(usage => usage.employeeId))),
    [usagesOnOrAfterBoundary]
  );
  const [{ data: employeesByIdsData }] = useGetEmployeesByIdsQuery({
    variables: { ids: employeeIds },
    pause: !open || employeeIds.length === 0
  });
  const [{ data: locationsData }] = useGetLocationsQuery({
    pause: !open || usagesOnOrAfterBoundary.length === 0
  });
  const employeeNameById = useMemo(() => {
    const employees = employeesByIdsData?.employeesByIds ?? [];
    return new Map(
      employees
        .filter((employee): employee is NonNullable<typeof employee> => Boolean(employee?.identityId))
        .map(employee => [
          employee.identityId!,
          constructNameEN(employee.firstName, employee.lastName ?? '').trim() ||
            strings.deleteLeaveTypeDialog.unknownEmployee
        ])
    );
  }, [employeesByIdsData]);
  const locationNameById = useMemo(() => {
    const locations = locationsData?.locations ?? [];
    return new Map(
      locations
        .filter((location): location is NonNullable<typeof location> => Boolean(location))
        .map(location => [location.id, location.name])
    );
  }, [locationsData]);

  const title = leaveType
    ? strings.deleteLeaveTypeDialog.title(leaveType.displayName.label || leaveType.name.label)
    : undefined;

  const formatUsageDate = (date: string) => new Date(`${date}T00:00:00`).toLocaleDateString();

  const body = deleteValidationLoading ? (
    <p className="text-sm text-secondary-std">{strings.deleteLeaveTypeDialog.loading}</p>
  ) : hasUsagesOnOrAfterBoundary ? (
    <div className="space-y-3">
      <p className="text-sm text-primary-std">{strings.deleteLeaveTypeDialog.futureMessage}</p>
      <div>
        <p className="text-sm font-medium text-primary-std">{strings.deleteLeaveTypeDialog.futureListHeading}</p>
        <ul className="mt-2 max-h-72 space-y-2 overflow-y-auto rounded border border-primary-std p-3 text-sm text-primary-std">
          {usagesOnOrAfterBoundary.map(usage => (
            <li key={usage.availabilityExceptionId}>
              {[
                formatUsageDate(usage.date),
                usage.locationId != null ? locationNameById.get(usage.locationId) : null,
                employeeNameById.get(usage.employeeId) ?? strings.deleteLeaveTypeDialog.unknownEmployee
              ]
                .filter(Boolean)
                .join(' ')}
            </li>
          ))}
        </ul>
      </div>
    </div>
  ) : hasUsagesBeforeBoundary ? (
    <p className="text-sm text-primary-std">{strings.deleteLeaveTypeDialog.pastMessage}</p>
  ) : (
    <p className="text-sm text-primary-std">{strings.deleteLeaveTypeDialog.message}</p>
  );

  return (
    <ConfirmDialog
      title={title}
      confirmLabel={strings.deleteLeaveTypeDialog.confirm}
      cancelLabel={strings.editLeaveType.cancel}
      dangerous
      loading={deletingLeaveType}
      confirmDisabled={deleteValidationLoading || hasUsagesOnOrAfterBoundary}
      open={open}
      onClose={onClose}
      onConfirm={handleDelete}
    >
      {body}
    </ConfirmDialog>
  );
};
